import type { CSSProperties, ElementType, ReactNode, Ref } from 'react';
import { clsx } from 'clsx';
import { buildResponsiveProps, type ResponsiveBoxSizing } from '../../util/responsive';
import { sanitizeCSSVariables } from '../../util/style';
import { VerticalStack } from '../alignment/vertical-stack';
import type { Polymorphic } from '../polymorphism/polymorphic';
import { CardTitle } from './card-title';
import styles from './card.module.scss';

const componentStyleName = 'card';

const defaultPadding: ResponsiveBoxSizing = {
  top: 0,
  right: 4,
  bottom: 0,
  left: 4,
};

const defaultHeaderPadding: ResponsiveBoxSizing = {
  top: 2,
  right: 4,
  bottom: 2,
  left: 4,
};

export const CARD_FULL_PADDING: ResponsiveBoxSizing = {
  top: 4,
  right: 4,
  bottom: 4,
  left: 4,
};

export const CARD_ONLY_HORIZONTAL_PADDING: ResponsiveBoxSizing = {
  top: 0,
  right: 4,
  bottom: 0,
  left: 4,
};

export const CARD_ONLY_VERTICAL_PADDING: ResponsiveBoxSizing = {
  top: 4,
  right: 0,
  bottom: 4,
  left: 0,
};

export const CARD_NO_PADDING: ResponsiveBoxSizing = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

const DEFAULT_CARD_ELEMENT = 'div';

type DefaultCardElement = typeof DEFAULT_CARD_ELEMENT;

type CardElement = 'div' | 'button';

interface BaseCardProps {
  as?: CardElement;
  headerPadding?: ResponsiveBoxSizing;
  isBorderless?: boolean;
  padding?: ResponsiveBoxSizing;
  ref?: Ref<HTMLDivElement>;
  title?: ReactNode;
}

export type CardProps<AsComponent extends ElementType = DefaultCardElement> = Polymorphic.Props<AsComponent, BaseCardProps>;

export function Card<AsComponent extends ElementType = DefaultCardElement>({
  as,
  className,
  children,
  isBorderless,
  padding = defaultPadding,
  headerPadding = defaultHeaderPadding,
  title,
  ...rest
}: CardProps<AsComponent>) {
  const Component = (as ?? DEFAULT_CARD_ELEMENT) satisfies ElementType;
  const baseStyle = sanitizeCSSVariables({
    ...buildResponsiveProps(componentStyleName, 'padding-top', padding?.top, 'space'),
    ...buildResponsiveProps(componentStyleName, 'padding-right', padding?.right, 'space'),
    ...buildResponsiveProps(componentStyleName, 'padding-bottom', padding?.bottom, 'space'),
    ...buildResponsiveProps(componentStyleName, 'padding-left', padding?.left, 'space'),
  }) as CSSProperties;

  const base = (
    <div className={styles.content} style={baseStyle}>
      {children}
    </div>
  );

  return (
    <Component className={clsx(styles.card, isBorderless && styles.isBorderless, className)} {...rest}>
      {title ? (
        <VerticalStack>
          <CardTitle padding={headerPadding}>{title}</CardTitle>
          {base}
        </VerticalStack>
      ) : (
        base
      )}
    </Component>
  );
}
